import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useUpdateUserSetting, useUserSettingDetail } from '../api';
import { asFormString } from '../format';
import { updateUserSettingSchema, type UpdateUserSettingInput } from '../schema';

/* Margin select options: backend stores 1..10, displays as 10%..100%. */
const MARGIN_OPTIONS = Array.from({ length: 10 }, (_, i) => ({
  value: String(i + 1),
  label: `${(i + 1) * 10}%`,
}));

/* Interval select options: 1..9 seconds. */
const INTERVAL_OPTIONS = Array.from({ length: 9 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}s`,
}));

export function UpdateUserSettingPage() {
  const navigate = useNavigate();
  const { subId: subIdParam, userId: userIdParam } = useParams<{
    subId: string;
    userId: string;
  }>();
  const subId = Number(subIdParam ?? 0);
  const targetUserId = Number(userIdParam ?? 0);

  const detail = useUserSettingDetail(subId, targetUserId);
  const update = useUpdateUserSetting();

  const form = useForm<UpdateUserSettingInput>({
    resolver: zodResolver(updateUserSettingSchema),
    mode: 'onTouched',
    defaultValues: {
      tier1: '1',
      t1_margin: '',
      t1_interval: '',
      t1_threshold: '',
      tier2: '1',
      t2_margin: '',
      t2_start_interval: '',
      t2_end_interval: '',
      t2_threshold: '',
      tier3: '1',
      t3_margin: '',
      t3_start_interval: '',
      t3_end_interval: '',
      t3_threshold: '',
      tier4: '1',
      t4_margin: '',
      t4_interval: '',
      t4_threshold: '',
    },
  });

  /* Prefill from the existing record. */
  useEffect(() => {
    if (!detail.data) return;
    const d = detail.data;
    form.reset({
      tier1: (d.tier1 ?? '1') as '1' | '2',
      t1_margin: asFormString(d.t1_margin),
      t1_interval: asFormString(d.t1_interval),
      t1_threshold: asFormString(d.t1_threshold),
      tier2: (d.tier2 ?? '1') as '1' | '2',
      t2_margin: asFormString(d.t2_margin),
      t2_start_interval: asFormString(d.t2_start_interval),
      t2_end_interval: asFormString(d.t2_end_interval),
      t2_threshold: asFormString(d.t2_threshold),
      tier3: (d.tier3 ?? '1') as '1' | '2',
      t3_margin: asFormString(d.t3_margin),
      t3_start_interval: asFormString(d.t3_start_interval),
      t3_end_interval: asFormString(d.t3_end_interval),
      t3_threshold: asFormString(d.t3_threshold),
      tier4: (d.tier4 ?? '1') as '1' | '2',
      t4_margin: asFormString(d.t4_margin),
      t4_interval: asFormString(d.t4_interval),
      t4_threshold: asFormString(d.t4_threshold),
    });
  }, [detail.data, form]);

  const tier1On = form.watch('tier1') === '2';
  const tier2On = form.watch('tier2') === '2';
  const tier3On = form.watch('tier3') === '2';
  const tier4On = form.watch('tier4') === '2';

  const onSubmit = (input: UpdateUserSettingInput) => {
    update.mutate(
      {
        userId: targetUserId,
        subId,
        tier1: input.tier1,
        t1_margin: input.tier1 === '2' ? (input.t1_margin ?? '') : '',
        t1_interval: input.tier1 === '2' ? (input.t1_interval ?? '') : '',
        t1_threshold: input.tier1 === '2' ? (input.t1_threshold ?? '') : '',
        tier2: input.tier2,
        t2_margin: input.tier2 === '2' ? (input.t2_margin ?? '') : '',
        t2_start_interval: input.tier2 === '2' ? (input.t2_start_interval ?? '') : '',
        t2_end_interval: input.tier2 === '2' ? (input.t2_end_interval ?? '') : '',
        t2_threshold: input.tier2 === '2' ? (input.t2_threshold ?? '') : '',
        tier3: input.tier3,
        t3_margin: input.tier3 === '2' ? (input.t3_margin ?? '') : '',
        t3_start_interval: input.tier3 === '2' ? (input.t3_start_interval ?? '') : '',
        t3_end_interval: input.tier3 === '2' ? (input.t3_end_interval ?? '') : '',
        t3_threshold: input.tier3 === '2' ? (input.t3_threshold ?? '') : '',
        tier4: input.tier4,
        t4_margin: input.tier4 === '2' ? (input.t4_margin ?? '') : '',
        t4_interval: input.tier4 === '2' ? (input.t4_interval ?? '') : '',
        t4_threshold: input.tier4 === '2' ? (input.t4_threshold ?? '') : '',
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'User settings updated');
          navigate('/user-settings');
        },
        onError: (err) => {
          toast.error(readErrorMessage(err) ?? 'Could not save the settings.');
        },
      },
    );
  };

  if (detail.isLoading) {
    return (
      <ScrollPage>
        <PageHeader title="Update User Settings" description="Loading…" />
        <FormSkeleton />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Update User Settings"
        description={
          detail.data
            ? `Editing tier-pricing for ${detail.data.username}.`
            : 'Editing tier-pricing.'
        }
        actions={
          <Button asChild variant="outline">
            <Link to="/user-settings">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {/* Tier 1 */}
          <TierCard
            title="Tier 1"
            description="Single-interval billing margin applied when call duration crosses the threshold."
            enabled={tier1On}
            onToggle={(on) => form.setValue('tier1', on ? '2' : '1', { shouldValidate: true })}
          >
            <FormField
              control={form.control}
              name="t1_margin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Margin</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a margin" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MARGIN_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t1_interval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Interval</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick an interval" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INTERVAL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t1_threshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Threshold</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      type="number"
                      inputMode="numeric"
                      min="0"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TierCard>

          {/* Tier 2 */}
          <TierCard
            title="Tier 2"
            description="Range-based billing margin. Margin applies when call duration falls between start and end intervals."
            enabled={tier2On}
            onToggle={(on) => form.setValue('tier2', on ? '2' : '1', { shouldValidate: true })}
          >
            <FormField
              control={form.control}
              name="t2_margin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Margin</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a margin" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MARGIN_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t2_start_interval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start interval</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Start" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INTERVAL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t2_end_interval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End interval</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="End" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INTERVAL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t2_threshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Threshold</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      type="number"
                      inputMode="numeric"
                      min="0"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TierCard>

          {/* Tier 3 — identical shape to Tier 2. */}
          <TierCard
            title="Tier 3"
            description="Second range-based tier. Same shape as Tier 2 — independent thresholds apply."
            enabled={tier3On}
            onToggle={(on) => form.setValue('tier3', on ? '2' : '1', { shouldValidate: true })}
          >
            <FormField
              control={form.control}
              name="t3_margin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Margin</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a margin" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MARGIN_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t3_start_interval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start interval</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Start" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INTERVAL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t3_end_interval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>End interval</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="End" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INTERVAL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t3_threshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Threshold</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      type="number"
                      inputMode="numeric"
                      min="0"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TierCard>

          {/* Tier 4 */}
          <TierCard
            title="Tier 4"
            description="Single-interval billing margin applied after the threshold."
            enabled={tier4On}
            onToggle={(on) => form.setValue('tier4', on ? '2' : '1', { shouldValidate: true })}
          >
            <FormField
              control={form.control}
              name="t4_margin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Margin</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick a margin" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MARGIN_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t4_interval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Interval</FormLabel>
                  <Select value={field.value ?? ''} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Pick an interval" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INTERVAL_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="t4_threshold"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Threshold</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      type="number"
                      inputMode="numeric"
                      min="0"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </TierCard>

          <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button asChild variant="ghost">
              <Link to="/user-settings">Cancel</Link>
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save settings
            </Button>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

interface TierCardProps {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (on: boolean) => void;
  children: React.ReactNode;
}

function TierCard({ title, description, enabled, onToggle, children }: TierCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle>{title}</CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} aria-label={`Enable ${title}`} />
      </CardHeader>
      {enabled && (
        <CardContent className="grid gap-4 border-t pt-4 md:grid-cols-2 lg:grid-cols-4">
          {children}
        </CardContent>
      )}
    </Card>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3, 4].map((s) => (
        <Card key={s}>
          <CardContent className="space-y-3 p-6">
            <Skeleton className="h-5 w-1/4" />
            <div className="grid gap-4 md:grid-cols-4">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
